import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Property } from '../types.ts';
import PropertyCard from '../components/PropertyCard.tsx';
import { Map, List as ListIcon, Filter } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';

const formatPrice = (price: number) => {
  if (price >= 1000000) return `${(price / 1000000).toFixed(1)}M`;
  if (price >= 1000) return `${(price / 1000).toFixed(0)}k`;
  return price.toString();
};

const DefaultIcon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const createPriceIcon = (price: number, isSelected: boolean) => {
  return L.divIcon({
    className: 'custom-price-marker',
    html: `<div class="${
      isSelected 
        ? 'bg-slate-900 text-gold-400 scale-125 z-[100] ring-1 ring-gold-400 font-bold' 
        : 'bg-white text-slate-900 hover:scale-110 hover:z-50'
    } px-2 py-1 rounded-sm shadow-sm border border-slate-200 text-[10px] whitespace-nowrap transition-all flex items-center justify-center font-sans">
      ${formatPrice(price)}
    </div>`,
    iconSize: [40, 30],
    iconAnchor: [20, 15]
  });
};

interface ListingsProps {
  type: 'sale' | 'rent' | 'off-plan' | 'uk-investment';
  properties: Property[];
}

const MapUpdater = ({ center }: { center: [number, number] }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const Listings: React.FC<ListingsProps> = ({ type, properties }) => {
  const [showMap, setShowMap] = useState(true);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const searchLocation = searchParams.get('search');

  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    beds: 'any',
    category: 'any'
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const filteredProperties = useMemo(() => {
    let props = properties;
    if (type === 'uk-investment') {
      props = props.filter(p => p.location.includes('United Kingdom') || p.type === 'uk-investment');
    } else {
      props = props.filter(p => p.type === type);
    }

    if (filters.minPrice) props = props.filter(p => p.price >= Number(filters.minPrice));
    if (filters.maxPrice) props = props.filter(p => p.price <= Number(filters.maxPrice));
    if (filters.beds !== 'any') {
      if (filters.beds === '5+') props = props.filter(p => p.beds >= 5);
      else props = props.filter(p => p.beds === Number(filters.beds));
    }
    if (filters.category !== 'any') {
      const cat = filters.category.toLowerCase();
      props = props.filter(p => p.title.toLowerCase().includes(cat) || p.propertyType.toLowerCase().includes(cat));
    }
    if (searchLocation) {
        const query = searchLocation.toLowerCase();
        props = props.filter(p => 
            p.location.toLowerCase().includes(query) || 
            p.title.toLowerCase().includes(query)
        );
    }
    return props;
  }, [type, properties, filters, searchLocation]);

  const getPageTitle = () => {
    if (searchLocation) return `Properties in ${searchLocation}`;
    switch(type) {
      case 'sale': return 'Luxury Sales Portfolio';
      case 'rent': return 'Premium Rentals';
      case 'off-plan': return 'Off-Plan Investments';
      case 'uk-investment': return 'International Assets';
      default: return 'Properties';
    }
  };

  const defaultCenter: [number, number] = type === 'uk-investment' ? [53.4808, -2.2426] : [25.1124, 55.1390];
  const mapCenter: [number, number] = filteredProperties.length > 0 && filteredProperties[0].lat && filteredProperties[0].lng
    ? [filteredProperties[0].lat, filteredProperties[0].lng] 
    : defaultCenter;

  const handleMarkerClick = (id: string) => {
    setSelectedPropertyId(id);
    const element = document.getElementById(`property-card-${id}`);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="pt-24 md:pt-28 h-screen flex flex-col bg-white overflow-hidden">
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex flex-col gap-4 shrink-0 shadow-sm z-30">
         <div className="flex justify-between items-center">
            <div>
                <h1 className="text-xl md:text-2xl font-serif text-slate-900 leading-tight">{getPageTitle()}</h1>
                <p className="text-[9px] uppercase font-bold tracking-widest text-gold-600 mt-1">{filteredProperties.length} Handpicked Assets Available</p>
            </div>
            <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowMap(!showMap)}
                  className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-sm text-[10px] font-bold uppercase tracking-widest hover:bg-gold-500 transition-all md:hidden"
                >
                    {showMap ? <><ListIcon size={14}/> List</> : <><Map size={14}/> Map</>}
                </button>
                <div className="hidden md:flex bg-slate-100 p-1 rounded-sm gap-1">
                   <button onClick={()=>setShowMap(false)} className={`p-1.5 rounded-sm ${!showMap ? 'bg-white shadow-sm text-slate-900' : 'text-gray-400'}`}><ListIcon size={16}/></button>
                   <button onClick={()=>setShowMap(true)} className={`p-1.5 rounded-sm ${showMap ? 'bg-white shadow-sm text-slate-900' : 'text-gray-400'}`}><Map size={16}/></button>
                </div>
            </div>
         </div>
         <div className="flex flex-wrap items-center gap-3">
            <select value={filters.minPrice} onChange={e=>setFilters({...filters, minPrice: e.target.value})} className="text-[10px] border border-gray-200 rounded-sm px-3 py-2 bg-slate-50 outline-none focus:border-gold-500 font-bold uppercase tracking-widest">
               <option value="">Min Price</option>
               <option value="500000">500k AED</option>
               <option value="1000000">1M AED</option>
               <option value="5000000">5M AED</option>
            </select>
            <select value={filters.maxPrice} onChange={e=>setFilters({...filters, maxPrice: e.target.value})} className="text-[10px] border border-gray-200 rounded-sm px-3 py-2 bg-slate-50 outline-none focus:border-gold-500 font-bold uppercase tracking-widest">
               <option value="">Max Price</option>
               <option value="2000000">2M AED</option>
               <option value="5000000">5M AED</option>
               <option value="10000000">10M+ AED</option>
            </select>
            <select value={filters.beds} onChange={e=>setFilters({...filters, beds: e.target.value})} className="text-[10px] border border-gray-200 rounded-sm px-3 py-2 bg-slate-50 outline-none focus:border-gold-500 font-bold uppercase tracking-widest">
               <option value="any">Beds</option>
               {[1,2,3,4,'5+'].map(n => <option key={n} value={n}>{n} Bed</option>)}
            </select>
         </div>
      </div>

      <div className="flex-grow flex relative overflow-hidden">
        <div className={`${showMap ? 'block' : 'hidden md:block'} flex-grow h-full relative z-10 bg-slate-100 order-1`}>
           <MapContainer 
             center={mapCenter} 
             zoom={type === 'uk-investment' ? 6 : 12} 
             className="h-full w-full"
           >
              <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
              <MapUpdater center={mapCenter} />
              {filteredProperties.map(prop => (
                prop.lat && prop.lng && (
                  <Marker 
                    key={prop.id} 
                    position={[prop.lat, prop.lng]} 
                    icon={createPriceIcon(prop.price, selectedPropertyId === prop.id)}
                    eventHandlers={{ click: () => handleMarkerClick(prop.id) }}
                  />
                )
              ))}
           </MapContainer>
        </div>
        <div 
          ref={listRef}
          className={`${!showMap ? 'block' : 'hidden md:block'} w-full md:w-[450px] lg:w-[550px] h-full overflow-y-auto p-4 md:p-6 bg-slate-50 order-2 border-l border-gray-100 no-scrollbar`}
        >
            {filteredProperties.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 pb-24">
                  {filteredProperties.map((prop) => (
                    <div 
                      id={`property-card-${prop.id}`} 
                      key={prop.id} 
                      className={`transition-all duration-300 ${selectedPropertyId === prop.id ? 'ring-2 ring-gold-500 scale-[1.01]' : ''}`}
                      onClick={() => setSelectedPropertyId(prop.id)}
                    >
                      <PropertyCard property={prop} />
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-32">
                  <p className="text-sm font-serif text-gray-400 italic">No assets matching your criteria were found in this sector.</p>
                  <button onClick={()=>setFilters({minPrice:'', maxPrice:'', beds:'any', category:'any'})} className="mt-4 text-[10px] font-bold uppercase text-gold-600 underline tracking-widest">Clear All Filters</button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Listings;